import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    clearFacetValues,
    selectFacetValue
} from "@/redux/modules/value/search/search.actions";
import {
    getSelectedValuesForFacet,
    useSearchComponentStateSelector
} from "@/redux/modules/value/search/search.selectors";
import {
    GetStoreStrategy,
    StorePersistenceStrategy,
    StoreService
} from "@/services/store.service";
import * as AvaibleInStoreTracker from "@/services/tracking/trackers/feature/availableInStore.tracker";
import * as SelectFacetTracker from "@/services/tracking/trackers/feature/selectFacet.tracker";
import { StorePickerModalStates } from "@/utils/enums";
import { useStore } from "@/utils/hooks/store/useStore.hook";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";
import { SearchComponent } from "@/utils/search/types/searchComponent";

import {
    FACET_DELIMITER,
    getOperator,
    SHOW_PRODUCTS_FACET
} from "../../StateComponents/Facets/Facet/facet.configuration";
import {
    createStoreStockFacetValue,
    isShowProductsFacet,
    isStoreStockFacetValue
} from "./helpers/facet.helpers";

const component = SearchComponent.PRODUCT_FACETS;
const facetName = SHOW_PRODUCTS_FACET;

const operator = getOperator(facetName);
const invalidations = getInvalidationsForComponent(component);

export const useAvailableInStore = () => {
    const dispatch = useThunkDispatch();

    const { store, storeCode, storeLoading } = useStore({
        strategy: GetStoreStrategy.Session
    });

    const facets = useSearchComponentStateSelector(component);

    const selectedValues = useSelector(
        getSelectedValuesForFacet(component, facetName),
        shallowEqual
    ).filter(isStoreStockFacetValue);

    const showProductsFacet = facets?.facets.find(isShowProductsFacet);

    const facetValue = createStoreStockFacetValue(store?.code);
    const selected = selectedValues.includes(facetValue);
    const facetValuesCount = showProductsFacet?.values[facetValue] ?? 0;

    const [storePickerModalState, setStorePickerModalState] = useState(
        StorePickerModalStates.Closed
    );

    const [isCheckedOptimistic, setIsCheckedOptimistic] =
        useState<boolean>(selected);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setIsCheckedOptimistic(selected);
    }, [selected, setIsCheckedOptimistic]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        // Loads the preferred store if the url params contain a store facet
        // and the session isn't known

        if (storeLoading) return;
        if (isDefined(store)) return;
        if (isEmpty(selectedValues)) return;
        // Session store will be loaded by useStore hook
        // No manual loading of the selectedValues store needed
        if (storeCode) return;

        const [_, storeId] = selectedValues.at(0)?.split(FACET_DELIMITER) ?? [];
        if (!storeId) return;

        StoreService.setStoreCode(StorePersistenceStrategy.Session, storeId);
    }, [store, storeLoading, selectedValues]);

    const sendEvent = (facetName: string, facetValue: string) => {
        if (!showProductsFacet?.index) return;

        SelectFacetTracker.trackFacet(
            facetName,
            facetValue,
            showProductsFacet.index
        );
    };

    const clearStoreFacets = async () => {
        return dispatch(
            clearFacetValues(
                component,
                facetName,
                selectedValues,
                invalidations
            )
        );
    };

    const selectStoreFacet = async (value: string) => {
        return dispatch(
            selectFacetValue(
                component,
                {
                    name: facetName,
                    operator: operator
                },
                value,
                invalidations,
                sendEvent
            )
        );
    };

    const handleOnSelectStore = () => {
        setStorePickerModalState(StorePickerModalStates.Open);
    };

    const handleOnStorePickerModalSubmit = async (
        store: CloudStore,
        onClose?: () => void
    ) => {
        const newStoreFacetValue = createStoreStockFacetValue(store.code);
        onClose?.();

        await clearStoreFacets();

        if (facetValuesCount) {
            AvaibleInStoreTracker.trackOn(storeCode);
            await selectStoreFacet(newStoreFacetValue);
        }
    };

    const handleOnStorePickerModalClose = () =>
        setStorePickerModalState(StorePickerModalStates.Closed);

    const handleOnRefine = async () => {
        setIsCheckedOptimistic((isChecked) => !isChecked);

        if (selected) {
            AvaibleInStoreTracker.trackOff(storeCode);
            clearStoreFacets();
        } else {
            AvaibleInStoreTracker.trackOn(storeCode);
            selectStoreFacet(facetValue);
        }
    };

    return {
        preferredStore: store,
        storePickerModalState: storePickerModalState,
        selected: isCheckedOptimistic,
        count: facetValuesCount,
        onSelectStore: handleOnSelectStore,
        onStorePickerModalClose: handleOnStorePickerModalClose,
        onStorePickerModalSubmit: handleOnStorePickerModalSubmit,
        onRefine: handleOnRefine
    };
};
